/**
 * Subscription tiers for TENSION — single source of truth for marketing copy + feature gates.
 * Server-side enforcement still required for paid capabilities (AI, uploads, etc.).
 */

export type SubscriptionPlan = 'free' | 'basic' | 'pro';

export const SUBSCRIPTION_PLAN_VALUES = ['free', 'basic', 'pro'] as const satisfies ReadonlyArray<SubscriptionPlan>;

export const SUBSCRIPTION_PLAN_LABELS: Record<SubscriptionPlan, string> = {
  free: 'Awareness',
  basic: 'Control',
  pro: 'Transformation',
};

export interface PlanFeatures {
  dailyCheckIns: boolean;
  rtiHistoryDays: number | null; // null = unlimited
  patternInsights: 'none' | 'basic' | 'full';
  patternHoverDeepDive: boolean;
  tensionAI: boolean;
  journalVoiceNotes: boolean;
  journalGuidedPrompts: boolean;
  relationshipSlots: number;
  exportData: boolean;
  prioritySupport: boolean;
}

export const PLAN_FEATURES: Record<SubscriptionPlan, PlanFeatures> = {
  free: {
    dailyCheckIns: true,
    rtiHistoryDays: 14,
    patternInsights: 'basic',
    patternHoverDeepDive: false,
    tensionAI: false,
    journalVoiceNotes: false,
    journalGuidedPrompts: false,
    relationshipSlots: 1,
    exportData: false,
    prioritySupport: false,
  },
  basic: {
    dailyCheckIns: true,
    rtiHistoryDays: 90,
    patternInsights: 'basic',
    patternHoverDeepDive: true,
    tensionAI: false,
    journalVoiceNotes: false,
    journalGuidedPrompts: true,
    relationshipSlots: 2,
    exportData: true,
    prioritySupport: false,
  },
  pro: {
    dailyCheckIns: true,
    rtiHistoryDays: null,
    patternInsights: 'full',
    patternHoverDeepDive: true,
    tensionAI: true,
    journalVoiceNotes: true,
    journalGuidedPrompts: true,
    relationshipSlots: 5,
    exportData: true,
    prioritySupport: true,
  },
};

export function isSubscriptionPlan(value: unknown): value is SubscriptionPlan {
  return typeof value === 'string' && (SUBSCRIPTION_PLAN_VALUES as readonly string[]).includes(value);
}

export function normalizePlan(raw: string | null | undefined): SubscriptionPlan {
  const p = String(raw || 'free').toLowerCase();
  return isSubscriptionPlan(p) ? p : 'free';
}

export function planHasTensionAI(plan: SubscriptionPlan, subscriptionStatus?: string | null): boolean {
  if (plan !== 'pro') return false;
  const st = String(subscriptionStatus || '').toLowerCase();
  if (st === 'canceled' || st === 'past_due' || st === 'unpaid') return false;
  return true;
}
